/**
 * Свежесть данных раздела для панели фильтров.
 *
 * Раздел сообщает, на какие ответы он опирается; панель показывает самую
 * старую отметку `data_as_of` — худший случай, а не лучший. Если хоть один
 * из этих запросов упал, зелёной точки нет: «Данные 5 минут назад» рядом
 * с блоком «Не удалось получить данные» — неправда.
 *
 * Отметка — тоже чужие данные, поэтому при смене пользователя сбрасывается.
 */

import { useEffect, useState } from 'react'
import { onSessionChange } from '../api'
import { oldestAsOf, type ReportState } from './data'

export interface FreshnessValue {
  asOf: string | null
  /** Запрос раздела не выполнился: сервер недоступен или ответил ошибкой. */
  failed: boolean
}

const EMPTY: FreshnessValue = { asOf: null, failed: false }

let current: FreshnessValue = EMPTY
const listeners = new Set<(value: FreshnessValue) => void>()

function publish(value: FreshnessValue) {
  if (value.asOf === current.asOf && value.failed === current.failed) return
  current = value
  for (const listener of listeners) listener(value)
}

onSessionChange(() => publish(EMPTY))

export function usePublishFreshness(states: ReportState[]): void {
  const asOf = oldestAsOf(states)
  const failed = states.some((state) => state.status === 'error')
  useEffect(() => {
    // Пока новый ответ не пришёл, держим прежнюю отметку: она честна для
    // цифр, которые сейчас на экране.
    if (failed) publish({ asOf: current.asOf, failed: true })
    else if (asOf) publish({ asOf, failed: false })
  }, [asOf, failed])
}

export function useFreshness(): FreshnessValue {
  const [value, setValue] = useState(current)
  useEffect(() => {
    setValue(current)
    listeners.add(setValue)
    return () => {
      listeners.delete(setValue)
    }
  }, [])
  return value
}
